// Browser storage for express cart sessions.
import { getSessionToken } from "@/lib/utils";
//
// A brand link (/cart/:slug) keeps the session it created per slug, so opening
// that link again, or refreshing, reuses its session and cart. The agent's View
// Cart link (/cart) brings its own session; its token is kept per session so a
// refresh still works after the page removes it from the address bar.
//
// Storage can be unavailable (private browsing, blocked site data); every
// access falls back to memory so the page still works for that visit.

const SESSION_KEY_PREFIX = "express_slug_session:";

const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    memory.set(key, value);
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable; the memory copy is cleared below either way.
  }
  memory.delete(key);
}

export interface StoredExpressSession {
  session_id: string;
  session_token: string;
  expires_at: string;
}

function isStoredSession(value: unknown): value is StoredExpressSession {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.session_id === "string" && typeof v.session_token === "string" && typeof v.expires_at === "string";
}

/** The browser's express session for a link slug, if it hasn't expired. */
export function readExpressSession(slug: string): StoredExpressSession | null {
  const raw = read(SESSION_KEY_PREFIX + slug);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStoredSession(parsed) && Date.parse(parsed.expires_at) > Date.now()) return parsed;
  } catch {
    // Corrupt entry; drop it below.
  }
  remove(SESSION_KEY_PREFIX + slug);
  return null;
}

export function saveExpressSession(slug: string, session: StoredExpressSession): void {
  write(SESSION_KEY_PREFIX + slug, JSON.stringify(session));
}

export function clearExpressSession(slug: string): void {
  remove(SESSION_KEY_PREFIX + slug);
}

const SESSION_CART_TOKEN_PREFIX = "express_cart_token:";
const MAGENTO_CART_IDS_PREFIX = "magento_cart_ids:";

export type MagentoCartIds = Record<string, string>;

export function readMagentoCartIds(sessionId: string | null | undefined): MagentoCartIds {
  if (!sessionId) return {};
  const raw = read(MAGENTO_CART_IDS_PREFIX + sessionId);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([brandId, cartId]) =>
          brandId.length > 0 && typeof cartId === "string" && cartId.length > 0,
      ),
    );
  } catch {
    return {};
  }
}

export function saveMagentoCartIds(sessionId: string, cartIds: MagentoCartIds): void {
  write(MAGENTO_CART_IDS_PREFIX + sessionId, JSON.stringify(cartIds));
}

export function readSessionCartToken(sessionId: string): string | null {
  return read(SESSION_CART_TOKEN_PREFIX + sessionId);
}

export function saveSessionCartToken(sessionId: string, token: string): void {
  write(SESSION_CART_TOKEN_PREFIX + sessionId, token);
}

// Where "back to cart" goes for a session: /cart?session={id}. The saved path
// also retains its source marker when the user entered from a WhatsApp link.
const EXPRESS_RETURN_PREFIX = "express_cart_return:";

export function saveExpressCartReturn(sessionId: string, path: string): void {
  write(EXPRESS_RETURN_PREFIX + sessionId, path);
}

export function readExpressCartReturn(sessionId: string | null | undefined): string | null {
  if (!sessionId) return null;
  const path = read(EXPRESS_RETURN_PREFIX + sessionId);
  // Only same-site express cart paths.
  return path && path.startsWith("/cart") ? path : null;
}

/** The checkout's entry for a session, which always starts at shipping. */
export function checkoutEntryPath(sessionId: string): string {
  return `/order/${encodeURIComponent(sessionId)}`;
}

// A one-time message for the express cart when the checkout sends a shopper
// back to it, e.g. because stock ran out.
const EXPRESS_NOTICE_PREFIX = "express_cart_notice:";

export function saveExpressCartNotice(sessionId: string, message: string): void {
  write(EXPRESS_NOTICE_PREFIX + sessionId, message);
}

/** Returns the notice for this session once, then forgets it. */
export function takeExpressCartNotice(sessionId: string): string | null {
  const message = read(EXPRESS_NOTICE_PREFIX + sessionId);
  if (message) remove(EXPRESS_NOTICE_PREFIX + sessionId);
  return message;
}

/**
 * Sends a checkout session back to its standalone cart, with an optional
 * notice. Uses the remembered cart path when available, otherwise /cart.
 */
export function returnToExpressCart(sessionId: string | null | undefined, notice?: string | null): boolean {
  if (!sessionId) return false;
  const path = readExpressCartReturn(sessionId) ?? `/cart?session=${encodeURIComponent(sessionId)}`;
  try {
    const token = getSessionToken();
    if (token) saveSessionCartToken(sessionId, token);
  } catch {
    // Storage can be unavailable in private browsing or non-browser tests.
  }
  if (notice) saveExpressCartNotice(sessionId, notice);
  window.location.assign(path);
  return true;
}
